import React from 'react';
import styled from 'styled-components';

import { H1 } from '@/components/UI/Typography/Heading';
import { Flex } from '@/layout';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import useResize from '@/hooks/useResize';
import { endPblOcAssistanceSession } from '@/api/pbl.api';
import PATH from '@/router/path';
import QualificationReminder from '@/components/DmpV2/QualificationReminder';
import {
  clearPblAssistance,
  getPblAssistanceProfileId,
  getPblAssistanceSessionId,
  isPblAssistanceActive,
} from '@/utils/pblAssistance';
import { errorHandler } from '@/utils/toastHandler';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PBL_AUTHENTICATED_HEADER_MOBILE_MAX_WIDTH } from '@/layout/Header/headerLayout';

const Wrapper = styled.div<{
  isTablet: boolean;
  isMobile: boolean;
  centerContent: boolean;
  $isPblLayout: boolean;
}>`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  min-height: calc(100vh - 70px);
  padding: ${(p) =>
    p.isMobile
      ? '20px 15px'
      : p.$isPblLayout && p.isTablet
        ? '20px'
        : p.isTablet
          ? '20px clamp(20px, 5vw, 60px)'
          : p.centerContent
            ? '20px 0'
            : p.$isPblLayout
              ? '20px 40px 60px 300px'
              : '20px 60px 60px 300px'};
  background: ${(p) => (p.centerContent ? '#fff' : '#f5f5f5')};
  overflow-y: ${(p) => (p.centerContent ? 'unset' : 'initial')};

  .pageContainerTitle {
    min-width: 0;
    overflow-wrap: anywhere;
    font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.lg : p.theme.fontSizes.xl)};
    font-weight: 700;
    text-align: ${(p) => (p.isTablet && !p.$isPblLayout ? 'center' : 'left')};
  }
`;

const YellowPageWrapper = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: ${(p) => (p.isMobile ? '0' : '0 60px')};
  background: #f5f5f5;
  overflow-y: auto;
  margin: auto;
`;

const AssistanceBanner = styled.div`
  align-items: center;
  background: #fff3cd;
  border: 1px solid #d39e00;
  color: #5f4300;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 12px 16px;
  flex-wrap: wrap;
  gap: 12px;

  button { background: #153d7a; border: 0; color: #fff; cursor: pointer; padding: 8px 14px; }

  @media (max-width: 639px) {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;
    padding: 10px 12px;
    line-height: 1.35;
    button { align-self: flex-start; min-width: 112px; min-height: 38px; }
  }
`;

interface PageContainerProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  centerContent?: boolean;
}

const PageContainer = ({ title, children, centerContent = false }: PageContainerProps) => {
  const auth = useAppSelector(getAuthSelector);
  const { size, isMobile, isTablet } = useResize();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // @ts-ignore -- 舊 i18next 鍵型別超過目前 TypeScript 的遞迴推論限制。
  const { t } = useTranslation();
  const [assistanceActive, setAssistanceActive] = React.useState(isPblAssistanceActive());
  const isPblLayout = auth.isLogin && pathname.startsWith('/pbl');
  const useTabletPageLayout = isPblLayout
    ? size > 0
      ? size <= PBL_AUTHENTICATED_HEADER_MOBILE_MAX_WIDTH
      : isTablet
    : isTablet;

  const endAssistance = async () => {
    const sessionId = getPblAssistanceSessionId();
    const profileId = getPblAssistanceProfileId();
    if (!sessionId) return;
    const returnToCitizen = () => {
      clearPblAssistance();
      setAssistanceActive(false);
      navigate(profileId ? PATH.pbl.ocCitizenDetail.replace(':profileId', String(profileId)) : PATH.pbl.ocCitizens);
    };
    try {
      await endPblOcAssistanceSession(sessionId);
      returnToCitizen();
    } catch (error: any) {
      if ([401, 403, 404, 410, 422].includes(error.response?.status)) {
        returnToCitizen();
        return;
      }
      errorHandler(error.response?.data?.message || t('pbl.backlogEnhancements.assistance.endFailed'));
    }
  };

  if (title === 'e-Directory' && !auth.isLogin) return <YellowPageWrapper isMobile={isMobile} isTablet={isTablet}>{children}</YellowPageWrapper>;

  return (
    <Wrapper
      isMobile={isMobile}
      isTablet={useTabletPageLayout}
      centerContent={centerContent}
      $isPblLayout={isPblLayout}
    >
      {assistanceActive && isPblLayout ? (
        <AssistanceBanner role="status">
          <span>{t('pbl.backlogEnhancements.assistance.activeBanner')}</span>
          <button type="button" onClick={endAssistance}>{t('pbl.backlogEnhancements.assistance.end')}</button>
        </AssistanceBanner>
      ) : null}
      {title !== 'e-Directory' && (
        <Flex flexDirection={'column'} justifyContent={'flex-end'} gridGap={'10px'}>
          {React.isValidElement(title) ? title : <div className="pageContainerTitle">{title}</div>}
          {(isPblLayout ? !isMobile : !isTablet) && !centerContent && <hr style={{ marginBottom: '30px' }} />}
        </Flex>
      )}
      <QualificationReminder />
      {children}
    </Wrapper>
  );
};

export default PageContainer;
